import type { TipoAlvoImpacto } from './alvosImpacto'
import { calcularDanoSecundario, calcularDanoVeicular, type DanoVeicular } from './danoVeicular'
import { criarDescritorDeformacao, type DescritorDeformacaoVeicular } from './deformacaoVeicular'
import {
  CONFIGURACAO_OUTRO_VEICULO_PADRAO,
  ID_VEICULO_PRINCIPAL,
  resolverColisaoEntreVeiculos,
  zDoPontoLateral,
  type ColisaoEntreVeiculos,
  type ConfiguracaoOutroVeiculo,
} from './colisaoEntreVeiculos'
import type { PerfilVeiculo } from './perfilVeiculo'
import {
  amostrarCinematica,
  colisao,
  instantesDeContato,
  posicaoDoVeiculo,
  rolagemEm,
  type TipoColisao,
} from './timelineCinematica'

/**
 * Todos os descritores de deformação de um evento, para todos os veículos.
 *
 * É a ponte entre "o que aconteceu" (tipo, alvo, velocidade, variações) e
 * "o que cada veículo sofre". A cena só amostra no tempo e desenha. Puro e
 * determinístico: o mesmo evento produz sempre os mesmos descritores.
 */
export interface EntradaDeformacaoDoEvento {
  tipo: TipoColisao
  alvo: TipoAlvoImpacto
  perfil: PerfilVeiculo
  velocidadeKmh?: number
  smallOverlap?: boolean
  outroVeiculo?: ConfiguracaoOutroVeiculo
  /** duração normalizada do esmagamento */
  duracao?: number
}

export interface DeformacaoDoEvento {
  /** descritores de TODOS os veículos; filtre por `veiculoId` */
  descritores: readonly DescritorDeformacaoVeicular[]
  /** dano do veículo da maquete no primeiro contato */
  danoPrincipal: DanoVeicular
  /** colisão veículo × veículo do primeiro contato, se o alvo for outro veículo */
  colisaoPrincipal: ColisaoEntreVeiculos | null
  /** segundo contato do engavetamento (sempre outro veículo) */
  colisaoSecundaria: ColisaoEntreVeiculos | null
  /** x (frente/traseira) ou z (lateral) do contato no veículo da maquete, para posicionar o alvo */
  centroContatoPrincipal: number
  /**
   * Instante em que as superfícies se tocam na cena (ver `instanteDeToque`).
   * É onde o alvo é posicionado e onde a deformação começa.
   */
  instanteToque: number
}

export const DURACAO_ESMAGAMENTO = 0.28

/** Primeiro instante em que a rolagem passa de `limiteRad`: é quando o teto (ou a lateral) toca o solo. */
export function instanteDeRolagem(tipo: TipoColisao, limiteRad: number): number | null {
  for (let i = 0; i <= 200; i += 1) {
    const t = i / 200
    if (Math.abs(rolagemEm(tipo, t)) >= limiteRad) return t
  }
  return null
}

/**
 * Lado que desce primeiro na rolagem: rolagem positiva levanta +X, então −X
 * toca o solo; a negativa (tombamento sobre o motorista) desce +X.
 */
export const ladoQueDesce = (tipo: TipoColisao): 1 | -1 => (rolagemEm(tipo, 1) > 0 ? -1 : 1)

/** Face atingida a partir da direção da força (a força aponta para +X ⇒ contato na face −X). */
export const faceDaDirecao = (x: number): 1 | -1 => (x > 0 ? -1 : 1)

/**
 * Instante em que o veículo de fato encontra o que ele atinge.
 *
 * A timeline autoral começa a desacelerar o veículo no "Contato", mas ainda o
 * faz andar bem mais do que qualquer zona de absorção esmaga (na frontal, mais
 * de um metro). Com o alvo posto na face do "Contato", o carro atravessava o
 * muro. Aqui o toque é o instante em que o que falta andar até parar é
 * exatamente o que vai ser esmagado: ao fim, a chapa amassada encosta no muro
 * (ou na frente amassada do outro veículo), sem atravessar nada.
 *
 * Vale sempre que o veículo AVANÇA contra o alvo — sentido oposto ao da força
 * do impacto: frente contra muro/poste/veículo e lateral deslizando contra o
 * poste. Quando ele é empurrado para longe do contato (lateral em T, traseira,
 * abalroamento), o toque é o próprio "Contato".
 */
export function instanteDeToque(
  tipo: TipoColisao,
  perfil: PerfilVeiculo,
  instanteContato: number,
  esmagamentoM: number,
  velocidadeKmh?: number,
): { toque: number; parada: number } {
  const semAvanco = { toque: instanteContato, parada: instanteContato + DURACAO_ESMAGAMENTO }
  const direcao = colisao(tipo).direcaoImpacto
  if (!direcao) return semAvanco
  // quanto o veículo já andou contra o alvo (projeção no sentido oposto à força)
  const avanco = (t: number) => {
    const p = posicaoDoVeiculo(perfil, amostrarCinematica(tipo, t, velocidadeKmh))
    return -(p.x * direcao.x + p.z * direcao.z)
  }
  const final = avanco(1)
  if (final - avanco(instanteContato) < 0.02) return semAvanco
  const passos = 400
  let parada = 1
  for (let i = 0; i <= passos; i += 1) {
    const t = instanteContato + ((1 - instanteContato) * i) / passos
    if (final - avanco(t) < 0.004) {
      parada = t
      break
    }
  }
  let toque = instanteContato
  for (let i = 0; i <= passos; i += 1) {
    const t = instanteContato + ((parada - instanteContato) * i) / passos
    if (final - avanco(t) <= esmagamentoM) {
      toque = t
      break
    }
  }
  return { toque, parada: Math.max(parada, toque + 0.05) }
}

export function deformacaoDoEvento(entrada: EntradaDeformacaoDoEvento): DeformacaoDoEvento {
  const { tipo, alvo, perfil } = entrada
  const config = entrada.outroVeiculo ?? CONFIGURACAO_OUTRO_VEICULO_PADRAO
  const duracao = entrada.duracao ?? DURACAO_ESMAGAMENTO
  const definicao = colisao(tipo)
  const instantes = instantesDeContato(tipo)
  const t1 = instantes[0] ?? 0.22
  const t2 = instantes[1]
  const smallOverlap = Boolean(entrada.smallOverlap) && definicao.direcaoImpacto != null && definicao.direcaoImpacto.z < 0
  const descritores: DescritorDeformacaoVeicular[] = []

  // ---- primeiro contato
  // resolvido duas vezes: a primeira dá o esmagamento (não depende do
  // instante); a segunda posiciona B no instante em que as frentes se tocam
  const resolver = (instante: number, duracaoEsmagamento: number) =>
    resolverColisaoEntreVeiculos({
      tipo,
      perfilA: perfil,
      velocidadeEventoKmh: entrada.velocidadeKmh,
      config,
      smallOverlap,
      instanteContato: instante,
      duracaoDeformacao: duracaoEsmagamento,
    })
  const previa = alvo === 'outroVeiculo' && definicao.direcaoImpacto ? resolver(t1, duracao) : null

  let danoPrincipal = calcularDanoVeicular(tipo, alvo, entrada.velocidadeKmh)
  let centroContatoPrincipal = 0
  let instanteToque = t1

  const esmagamento = previa
    ? previa.descritorA.intrusaoMaxM + previa.descritorB.intrusaoMaxM
    : criarDescritorDeformacao(perfil, danoPrincipal, { smallOverlap }).intrusaoMaxM
  const { toque, parada } = instanteDeToque(tipo, perfil, t1, esmagamento, entrada.velocidadeKmh)
  const avancaContraOAlvo = toque !== t1 || parada !== t1 + DURACAO_ESMAGAMENTO
  instanteToque = toque
  const duracaoPrincipal = avancaContraOAlvo ? parada - toque : duracao
  const colisaoPrincipal = previa ? resolver(toque, duracaoPrincipal) : null

  if (colisaoPrincipal) {
    danoPrincipal = colisaoPrincipal.danoA
    descritores.push(colisaoPrincipal.descritorA, colisaoPrincipal.descritorB)
    const a = colisaoPrincipal.descritorA
    centroContatoPrincipal = a.centroFaceM ?? 0
  } else if (definicao.direcaoImpacto) {
    const lateral = Math.abs(definicao.direcaoImpacto.x) > Math.abs(definicao.direcaoImpacto.z)
    const zLateral = zDoPontoLateral(perfil, config.pontoLateral)
    const descritor = criarDescritorDeformacao(perfil, danoPrincipal, {
      veiculoId: ID_VEICULO_PRINCIPAL,
      instanteInicio: instanteToque,
      duracao: duracaoPrincipal,
      sinalLateral: lateral ? faceDaDirecao(definicao.direcaoImpacto.x) : 1,
      smallOverlap,
      centroFaceM: lateral ? zLateral : undefined,
    })
    descritores.push(descritor)
    centroContatoPrincipal = descritor.centroFaceM ?? 0
  }

  // ---- rolagem: o teto (tombamento: a borda do teto do lado apoiado)
  if (definicao.comRolagem) {
    const tombamento = tipo === 'tombamento'
    const tTeto = instanteDeRolagem(tipo, tombamento ? Math.PI / 2 - 0.12 : Math.PI * 0.8)
    if (tTeto != null) {
      const danoTeto = { ...calcularDanoVeicular(tipo, alvo, entrada.velocidadeKmh), regiao: 'teto' as const }
      descritores.push(
        criarDescritorDeformacao(perfil, danoTeto, {
          veiculoId: ID_VEICULO_PRINCIPAL,
          instanteInicio: Math.max(0, tTeto - 0.02),
          duracao: 0.12,
          sinalLateral: ladoQueDesce(tipo),
          variante: tombamento ? 'tombamento' : 'capotamento',
        }),
      )
    }
  }

  // ---- segundo contato (engavetamento)
  let colisaoSecundaria: ColisaoEntreVeiculos | null = null
  if (definicao.impactoSecundario && t2 != null) {
    colisaoSecundaria = resolverColisaoEntreVeiculos({
      tipo,
      perfilA: perfil,
      velocidadeEventoKmh: entrada.velocidadeKmh,
      config,
      secundario: true,
      instanteContato: t2,
      duracaoDeformacao: duracao,
    })
    if (colisaoSecundaria) {
      descritores.push(colisaoSecundaria.descritorA, colisaoSecundaria.descritorB)
    } else {
      const danoSecundario = calcularDanoSecundario(tipo, entrada.velocidadeKmh)
      if (danoSecundario) {
        descritores.push(
          criarDescritorDeformacao(perfil, danoSecundario, {
            veiculoId: ID_VEICULO_PRINCIPAL,
            instanteInicio: t2,
            duracao,
          }),
        )
      }
    }
  }

  return { descritores, danoPrincipal, colisaoPrincipal, colisaoSecundaria, centroContatoPrincipal, instanteToque }
}
